import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
  AutoAwesomeOutlined as CareIcon,
  CheckCircleRounded as SelectedIcon,
  DescriptionOutlined as DocumentIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { IMAGE_CAPTURE_INTENT_OPTIONS } from './imageCaptureIntent';
import colors from '../../assets/theme/colors';

const OPTION_ICONS = {
  save: SaveIcon,
  care: CareIcon,
  document: DocumentIcon,
};

const ImageCaptureIntentSelector = ({ value, onChange }) => (
  <Box sx={{ mt: 1.25 }}>
    <Typography sx={{ mb: 0.9, color: colors.semantic.ink2, fontSize: '0.9rem', fontWeight: 700 }}>
      How should Lifelog use this photo?
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: { xs: 0.7, sm: 0.9 },
      }}
    >
      {IMAGE_CAPTURE_INTENT_OPTIONS.map((option) => {
        const selected = value === option.value;
        const OptionIcon = OPTION_ICONS[option.icon] || DocumentIcon;
        return (
          <ButtonBase
            key={option.value}
            type="button"
            aria-pressed={selected}
            aria-label={option.label}
            onClick={() => onChange?.(option.value)}
            sx={{
              minWidth: 0,
              minHeight: { xs: 164, sm: 174 },
              alignItems: 'stretch',
              justifyContent: 'stretch',
              borderRadius: '18px',
              border: `1.5px solid ${selected ? alpha(colors.semantic.sage, 0.7) : colors.semantic.line2}`,
              bgcolor: selected ? alpha(colors.semantic.sage, 0.16) : colors.semantic.bg,
              color: colors.semantic.ink,
              textAlign: 'left',
              overflow: 'hidden',
              transition: 'background-color 140ms ease, border-color 140ms ease',
              '&:hover': {
                bgcolor: selected ? alpha(colors.semantic.sage, 0.22) : colors.semantic.sageMist,
              },
              '&:focus-visible': {
                outline: `3px solid ${alpha(colors.semantic.sage, 0.32)}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                p: { xs: 1, sm: 1.2 },
              }}
            >
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                <Box
                  sx={{
                    width: { xs: 38, sm: 42 },
                    height: { xs: 38, sm: 42 },
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: selected ? colors.semantic.bg : colors.semantic.sageMist,
                    color: selected ? colors.semantic.profileFlow.completionText : colors.semantic.ink2,
                    flexShrink: 0,
                  }}
                >
                  <OptionIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </Box>
                {selected ? (
                  <SelectedIcon
                    aria-hidden="true"
                    sx={{ color: colors.semantic.profileFlow.completionText, fontSize: { xs: 24, sm: 26 } }}
                  />
                ) : (
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: { xs: 22, sm: 24 },
                      height: { xs: 22, sm: 24 },
                      borderRadius: '50%',
                      border: `2px solid ${colors.semantic.line2}`,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>

              <Typography
                sx={{
                  mt: 1,
                  color: selected ? colors.semantic.profileFlow.completionText : colors.semantic.ink,
                  fontSize: { xs: '0.88rem', sm: '0.98rem' },
                  fontWeight: 750,
                  lineHeight: 1.18,
                }}
              >
                {option.label}
              </Typography>
              <Typography
                sx={{
                  mt: 0.55,
                  color: colors.semantic.ink2,
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  lineHeight: 1.32,
                }}
              >
                {option.description}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  </Box>
);

export default ImageCaptureIntentSelector;
